import React, { useEffect, useState, useMemo } from "react";
import { motion } from "framer-motion";
import {
  Plus,
  RefreshCw,
  Edit3,
  Trash2,
  CheckCircle2,
  Clock,
  AlertOctagon,
  Ban,
  FileText,
  Calendar,
  User,
} from "lucide-react";
import toast from "react-hot-toast";
import {
  card,
  microLabel,
  sectionTitle,
  accentBarSection,
  bodySecondary,
  motionStaggerChild,
  emptyState,
  iconSize,
} from "../../../../../lib/design-system";
import { cn } from "../../../../../lib/utils";
import {
  listATRs,
  deleteATR,
} from "../../../../../services/program.service";
import AtrForm from "../AtrForm";
import CloseAtrForm from "../CloseAtrForm";

const STATUS_META = {
  open: {
    label: "Open",
    Icon: AlertOctagon,
    bg: "bg-amber-50 dark:bg-amber-900/30",
    text: "text-amber-700 dark:text-amber-300",
    border: "border-amber-200 dark:border-amber-700",
  },
  in_progress: {
    label: "In Progress",
    Icon: Clock,
    bg: "bg-blue-50 dark:bg-blue-900/30",
    text: "text-blue-700 dark:text-blue-300",
    border: "border-blue-200 dark:border-blue-700",
  },
  closed: {
    label: "Closed",
    Icon: CheckCircle2,
    bg: "bg-emerald-50 dark:bg-emerald-900/30",
    text: "text-emerald-700 dark:text-emerald-300",
    border: "border-emerald-200 dark:border-emerald-700",
  },
  cancelled: {
    label: "Cancelled",
    Icon: Ban,
    bg: "bg-gray-50 dark:bg-gray-800",
    text: "text-gray-500 dark:text-gray-400",
    border: "border-gray-200 dark:border-gray-700",
  },
};

const STATUS_TABS = [
  { id: "all", label: "All" },
  { id: "open", label: "Open" },
  { id: "in_progress", label: "In Progress" },
  { id: "closed", label: "Closed" },
  { id: "cancelled", label: "Cancelled" },
];

const isOverdue = (atr) =>
  atr.dueDate &&
  ["open", "in_progress"].includes(atr.status) &&
  new Date(atr.dueDate) < new Date();

const AtrTab = ({ programId }) => {
  const [atrs, setAtrs] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState("all");
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [closing, setClosing] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  const fetchAtrs = async () => {
    try {
      setLoading(true);
      const data = await listATRs(programId);
      setAtrs(data.atrs || []);
      setCounts(data.counts || {});
      setError(null);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to load ATRs");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAtrs();
  }, [programId]);

  const filtered = useMemo(() => {
    if (filter === "all") return atrs;
    return atrs.filter((a) => a.status === filter);
  }, [atrs, filter]);

  const handleDelete = async (atr) => {
    if (
      !window.confirm(
        `Archive ATR for ${atr.outcome?.code}? Soft delete — record stays in DB for audit.`
      )
    )
      return;
    try {
      await deleteATR(atr._id);
      toast.success("ATR archived");
      fetchAtrs();
    } catch (err) {
      toast.error(err.response?.data?.error || "Archive failed");
    }
  };

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-6">
        <div>
          <h2 className={cn(sectionTitle, "text-xl sm:text-2xl")}>
            Action Taken Reports
          </h2>
          <div className={cn(accentBarSection, "mt-2")} />
          <p className={cn(bodySecondary, "mt-3 max-w-2xl")}>
            NBA Criterion 8 evidence. For each PO/PSO where attainment fell
            short, document the observation + planned action, assign an
            owner, then close with proof of intervention. NBA evaluators
            specifically ask: "what did you do about it?"
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={fetchAtrs}
            className="px-3 py-2 rounded-sm border border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            aria-label="Refresh"
          >
            <RefreshCw className={iconSize.md} />
          </button>
          <button
            onClick={() => {
              setEditing(null);
              setShowForm(true);
            }}
            className="inline-flex items-center gap-2 bg-accent1 text-white font-semibold px-4 py-2 rounded-sm hover:opacity-90 transition-opacity"
          >
            <Plus className={iconSize.md} />
            New ATR
          </button>
        </div>
      </div>

      {/* Summary chips */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-6">
        <SummaryChip label="Total" value={counts.total ?? 0} />
        <SummaryChip
          label="Open"
          value={counts.open ?? 0}
          color="text-amber-600"
        />
        <SummaryChip
          label="In Progress"
          value={counts.in_progress ?? 0}
          color="text-blue-600"
        />
        <SummaryChip
          label="Closed"
          value={counts.closed ?? 0}
          color="text-emerald-600"
        />
        <SummaryChip
          label="Overdue"
          value={counts.overdue ?? 0}
          color="text-red-600"
        />
      </div>

      {/* Status filter tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700 mb-5">
        <nav className="flex flex-wrap gap-1 -mb-px">
          {STATUS_TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setFilter(t.id)}
              className={cn(
                "px-3 py-2 text-xs uppercase tracking-wider font-bold border-b-2 transition-colors",
                filter === t.id
                  ? "border-accent1 text-accent1"
                  : "border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
              )}
            >
              {t.label}
            </button>
          ))}
        </nav>
      </div>

      {/* List */}
      {loading ? (
        <SkeletonList />
      ) : error ? (
        <div className={cn(emptyState, "border-red-200 dark:border-red-700")}>
          <div className="text-red-600 dark:text-red-400 font-semibold">
            {error}
          </div>
          <button
            onClick={fetchAtrs}
            className="mt-3 text-sm underline text-gray-600 dark:text-gray-300"
          >
            Retry
          </button>
        </div>
      ) : filtered.length === 0 ? (
        <div className={emptyState}>
          <FileText className="h-7 w-7 mx-auto text-gray-400 mb-3" />
          <div className="font-bold text-gray-700 dark:text-gray-200">
            {filter === "all"
              ? "No ATRs yet for this program"
              : `No ${filter.replace("_", " ")} ATRs`}
          </div>
          <div className={cn(bodySecondary, "mt-1")}>
            Click "New ATR", or go to the Attainment Dashboard and use the
            "Draft ATR" button on any red/amber outcome to pre-fill from
            actual numbers.
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((atr, i) => (
            <AtrRow
              key={atr._id}
              atr={atr}
              index={i}
              expanded={expandedId === atr._id}
              onToggle={() =>
                setExpandedId(expandedId === atr._id ? null : atr._id)
              }
              onEdit={() => {
                setEditing(atr);
                setShowForm(true);
              }}
              onDelete={() => handleDelete(atr)}
              onClose={() => setClosing(atr)}
            />
          ))}
        </div>
      )}

      {showForm && (
        <AtrForm
          programId={programId}
          atr={editing}
          onClose={(reload) => {
            setShowForm(false);
            setEditing(null);
            if (reload) fetchAtrs();
          }}
        />
      )}

      {closing && (
        <CloseAtrForm
          atr={closing}
          onClose={(reload) => {
            setClosing(null);
            if (reload) fetchAtrs();
          }}
        />
      )}
    </div>
  );
};

// ---------- Sub-components ----------

const SummaryChip = ({ label, value, color }) => (
  <div className={cn(card, "p-4")}>
    <div className={microLabel}>{label}</div>
    <div
      className={cn(
        "mt-1 text-2xl font-black tracking-tight",
        color || "text-gray-900 dark:text-white"
      )}
    >
      {value}
    </div>
  </div>
);

const AtrRow = ({ atr, index, expanded, onToggle, onEdit, onDelete, onClose }) => {
  const meta = STATUS_META[atr.status] || STATUS_META.open;
  const StatusIcon = meta.Icon;
  const overdue = isOverdue(atr);

  return (
    <motion.div
      {...motionStaggerChild(index, 0)}
      className={cn(card, "p-5 group")}
    >
      <div className="flex items-start gap-4">
        <div className="flex-shrink-0">
          <div className="h-1 w-8 bg-accent1 rounded-sm mb-2" />
          <div className={microLabel}>{atr.outcome?.code || "?"}</div>
          <div className="text-[10px] uppercase tracking-wider font-bold text-gray-400 mt-0.5">
            {atr.outcomeType}
          </div>
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <span
              className={cn(
                "inline-flex items-center gap-1 px-2.5 py-1 rounded-sm border text-xs font-medium",
                meta.bg,
                meta.border,
                meta.text
              )}
            >
              <StatusIcon className="h-3 w-3" />
              {meta.label}
            </span>
            {overdue && (
              <span className="px-2.5 py-1 rounded-sm bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-700 text-xs font-bold uppercase tracking-wider">
                Overdue
              </span>
            )}
            {atr.outcome?.category && (
              <span className="text-xs font-bold text-gray-700 dark:text-gray-300">
                {atr.outcome.category}
              </span>
            )}
            {atr.batch && (
              <span className="text-xs text-gray-500 dark:text-gray-400">
                Batch {atr.batch}
              </span>
            )}
          </div>

          <p className="text-sm text-gray-800 dark:text-gray-200 line-clamp-2">
            {atr.observation}
          </p>

          <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-gray-500 dark:text-gray-400">
            {atr.ownerName && (
              <span className="inline-flex items-center gap-1">
                <User className="h-3 w-3" /> {atr.ownerName}
              </span>
            )}
            {atr.dueDate && (
              <span className="inline-flex items-center gap-1">
                <Calendar className="h-3 w-3" /> due{" "}
                {new Date(atr.dueDate).toLocaleDateString()}
              </span>
            )}
            {atr.observedAttainment != null && atr.targetAttainment != null && (
              <span>
                Observed{" "}
                <span className="font-mono font-semibold">
                  {Number(atr.observedAttainment).toFixed(2)}
                </span>{" "}
                vs target{" "}
                <span className="font-mono font-semibold">
                  {Number(atr.targetAttainment).toFixed(2)}
                </span>
              </span>
            )}
          </div>

          <button
            onClick={onToggle}
            className="mt-3 text-xs font-semibold text-accent1 hover:underline"
          >
            {expanded ? "Hide details" : "Show details"}
          </button>
        </div>

        {/* Right side: action buttons */}
        <div className="flex items-center gap-1 flex-shrink-0">
          {atr.status !== "closed" && atr.status !== "cancelled" && (
            <button
              onClick={onClose}
              className="px-3 py-1.5 rounded-sm bg-emerald-600 text-white text-xs font-semibold hover:opacity-90 transition-opacity"
              title="Close with notes"
            >
              <CheckCircle2 className="h-3.5 w-3.5 inline" /> Close
            </button>
          )}
          {atr.status !== "closed" && (
            <button
              onClick={onEdit}
              className="p-2 rounded-sm hover:bg-gray-100 dark:hover:bg-gray-700 opacity-0 group-hover:opacity-100 transition-opacity"
              title="Edit"
              aria-label="Edit"
            >
              <Edit3 className={iconSize.sm} />
            </button>
          )}
          <button
            onClick={onDelete}
            className="p-2 rounded-sm hover:bg-red-50 dark:hover:bg-red-900/20 text-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
            title="Archive"
            aria-label="Archive"
          >
            <Trash2 className={iconSize.sm} />
          </button>
        </div>
      </div>

      {expanded && (
        <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700 space-y-3 text-sm">
          {atr.outcome?.statement && (
            <DetailRow
              label="Outcome statement"
              text={atr.outcome.statement}
            />
          )}
          <DetailRow label="Observation" text={atr.observation} />
          {atr.rootCause && (
            <DetailRow label="Root cause" text={atr.rootCause} />
          )}
          <DetailRow label="Planned action" text={atr.plannedAction} />
          {atr.status === "closed" && (
            <>
              <DetailRow
                label="Closure notes"
                text={atr.closureNotes}
                emphasis
              />
              {atr.closedAttainment != null && (
                <DetailRow
                  label="Closed at level"
                  text={`${Number(atr.closedAttainment).toFixed(2)} ${
                    atr.observedAttainment != null
                      ? `(was ${Number(atr.observedAttainment).toFixed(2)} — Δ +${(
                          Number(atr.closedAttainment) -
                          Number(atr.observedAttainment)
                        ).toFixed(2)})`
                      : ""
                  }`}
                />
              )}
              {atr.closedAt && (
                <DetailRow
                  label="Closed on"
                  text={new Date(atr.closedAt).toLocaleString()}
                />
              )}
            </>
          )}
          <DetailRow
            label="Created"
            text={`${new Date(atr.createdAt).toLocaleDateString()}${
              atr.updatedAt && atr.updatedAt !== atr.createdAt
                ? ` · updated ${new Date(atr.updatedAt).toLocaleDateString()}`
                : ""
            }`}
          />
        </div>
      )}
    </motion.div>
  );
};

const DetailRow = ({ label, text, emphasis }) => (
  <div>
    <div className={microLabel}>{label}</div>
    <div
      className={cn(
        "mt-1 text-sm whitespace-pre-wrap",
        emphasis
          ? "text-gray-900 dark:text-white font-medium"
          : "text-gray-700 dark:text-gray-300"
      )}
    >
      {text}
    </div>
  </div>
);

const SkeletonList = () => (
  <div className="space-y-3">
    {[0, 1, 2].map((i) => (
      <div key={i} className={cn(card, "p-5")}>
        <div className="flex gap-4">
          <div className="space-y-2">
            <div className="h-1 w-8 bg-gray-200 dark:bg-gray-700 rounded-sm animate-pulse" />
            <div className="h-3 w-10 bg-gray-200 dark:bg-gray-700 rounded-sm animate-pulse" />
          </div>
          <div className="flex-1 space-y-2">
            <div className="h-3 w-1/3 bg-gray-200 dark:bg-gray-700 rounded-sm animate-pulse" />
            <div className="h-3 w-full bg-gray-100 dark:bg-gray-800 rounded-sm animate-pulse" />
            <div className="h-3 w-2/3 bg-gray-100 dark:bg-gray-800 rounded-sm animate-pulse" />
          </div>
        </div>
      </div>
    ))}
  </div>
);

export default AtrTab;
